import { has, omit } from "lodash";
import { from, Observable, of } from "rxjs";
import { Option } from "space-monad";
import { store } from "..";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { processErrorFromPromise } from "../errors/utils";
import { BillingActions } from "./actions";
import { BillingDTO } from "./models/dto";

export namespace BillingService {
  const api = new API("billing");

  export function getPricing(): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetPricing.T>
  > {
    return from(
      api
        .get(`pricing`)
        .then<FetchableSuccess<BillingDTO.Responses.GetPricing.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getBalance(): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetBalance.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return of({
        status: "error",
        err: new Error("User not logged in"),
      });

    return from(
      api
        .get(`billing/${login.data.accessToken.credentialsId}/balance`)
        .then<FetchableSuccess<BillingDTO.Responses.GetBalance.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getForexRate(): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetForexRate.T>
  > {
    return from(
      api
        .get(`billing/forex-rates`)
        .then<FetchableSuccess<BillingDTO.Responses.GetForexRate.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSKRTAddresses(): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetSKRTAddresses.T>
  > {
    return from(
      api
        .get(`billing/wallets/sekuritance`)
        .then<FetchableSuccess<BillingDTO.Responses.GetSKRTAddresses.T>>(
          (response) => ({
            status: "ready",
            data: response.body,
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function purchase(
    details: BillingDTO.Requests.Purchase.T
  ): Promise<BillingDTO.Responses.Purchase.T> {
    return api
      .post("billing/credits/purchases")
      .send(details)
      .then((res) => res.body)
      .then((body) => {
        store.dispatch(BillingActions.resetAllTransactions());
        store.dispatch(BillingActions.resetBalance());
        return body;
      })
      .catch<BillingDTO.Responses.Purchase.T>(processErrorFromPromise);
  }

  export function confirmPurchase(
    purchaseID: string,
    details: BillingDTO.Requests.ConfirmPurchase.T
  ): Promise<BillingDTO.Responses.ConfirmPurchase.T> {
    return api
      .post(`billing/credits/purchases/${purchaseID}/details`)
      .send(details)
      .then((res) => res.body)
      .catch<BillingDTO.Responses.Purchase.T>(processErrorFromPromise);
  }

  export function getAllTransactions(
    pagination: Pageable.T<
      keyof BillingDTO.Responses.GetSingleTransaction.T
    > = {}
  ): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetAllTransactions.T>
  > {
    return from(
      api
        .post(`billing/credits/purchases/search`)
        .query(Pageable.getQueryParam(omit(pagination, "filters")))
        .send(
          Option(pagination.filters)
            .map((f) =>
              f.reduce(
                (acc, curr) => ({ ...acc, [curr.field]: curr.value }),
                {}
              )
            )
            .getOrElse({})
        )
        .then<FetchableSuccess<BillingDTO.Responses.GetAllTransactions.T>>(
          (response) => ({
            status: "ready",
            data: BillingDTO.Responses.GetAllTransactions.parse(response.body),
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleTransaction(
    id: BillingDTO.Responses.GetSingleTransaction.T["id"],
    bustCache?: boolean
  ): Promise<BillingDTO.Responses.GetSingleTransaction.T> {
    const { singleTransactionMap } = store.getState().billing;

    if (
      !bustCache &&
      has(singleTransactionMap, id) &&
      singleTransactionMap[id] !== "LOADING"
    ) {
      return Promise.resolve(
        //TOOD fix for concurrency
        singleTransactionMap[id] as BillingDTO.Responses.GetSingleTransaction.T
      );
    }

    store.dispatch(BillingActions.getSingleTransaction(id));

    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return Promise.reject(new Error("User not logged in"));

    return api
      .get(`billing/credits/purchases/${id}`)
      .then<BillingDTO.Responses.GetSingleTransaction.T>((response) => {
        const parsed = BillingDTO.Responses.GetSingleTransaction.parse(
          response.body
        );
        store.dispatch(
          BillingActions.setSingleTransaction({
            id,
            result: parsed,
          })
        );
        return parsed;
      })
      .catch<BillingDTO.Responses.GetSingleTransaction.T>(
        processErrorFromPromise
      );
  }

  export function confirmTransaction(
    id: BillingDTO.Responses.GetSingleTransaction.T["id"],
    usdtAmount: number
  ): Promise<any> {
    return api
      .put(`billing/credits/purchases/${id}/confirm`)
      .send({ usdtAmount })
      .then<BillingDTO.Responses.ConfirmTransaction.T>((response) => {
        return response.body;
      })
      .then(async (body) => {
        store.dispatch(BillingActions.resetAllTransactions());
        store.dispatch(BillingActions.resetBalance());
        await getSingleTransaction(id, true);
        return body;
      })
      .catch<FetchableError>(processErrorFromPromise);
  }

  export function getAllUsages(
    pagination: Pageable.T<keyof BillingDTO.Responses.GetSingleUsage.T> = {}
  ): Observable<
    FetchableError | FetchableSuccess<BillingDTO.Responses.GetAllUsages.T>
  > {
    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return of({
        status: "error",
        err: new Error("User not logged in"),
      });

    return from(
      api
        .get(`billing/apis/${login.data.accessToken.credentialsId}`)
        .query(Pageable.getQueryParam(pagination))
        .then<FetchableSuccess<BillingDTO.Responses.GetAllUsages.T>>(
          (response) => ({
            status: "ready",
            data: BillingDTO.Responses.GetAllUsages.parse(response.body),
          })
        )
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getSingleUsage(
    id: BillingDTO.Responses.GetSingleUsage.T["id"]
  ): Promise<BillingDTO.Responses.GetSingleUsage.T> {
    const { singleUsageMap } = store.getState().billing;

    if (has(singleUsageMap, id) && singleUsageMap[id] !== "LOADING") {
      return Promise.resolve(
        //TOOD fix for concurrency
        singleUsageMap[id] as BillingDTO.Responses.GetSingleUsage.T
      );
    }

    store.dispatch(BillingActions.getSingleUsage(id));

    const login = store.getState().auth.login;
    if (login.status !== "ready")
      return Promise.reject(new Error("User not logged in"));

    return api
      .get(`billing/apis/${login.data.accessToken.credentialsId}/${id}`)
      .then<BillingDTO.Responses.GetSingleUsage.T>((response) => {
        const parsed = BillingDTO.Responses.GetSingleUsage.parse(response.body);

        store.dispatch(
          BillingActions.setSingleUsage({
            id,
            result: parsed,
          })
        );
        return parsed;
      })
      .catch<BillingDTO.Responses.GetSingleUsage.T>(processErrorFromPromise);
  }
}
